import React, { useEffect, useRef, useState } from 'react'
import { Tabs } from '@resurs/midas-react'
import { Conditional } from '@resurs/react-utils'
import { getText } from '../../../../../internal/TextLabels'
import DisabledOverlay from '../../../../../internal/DisabledOverlay'
import ApplicationSignPageAnalogUI from './ApplicationSignPageAnalogUI'
import SignDigitalPageUI from './SignDigitalPageUI'
import ApplicationSummaryBox from '../../shared/ApplicationSummaryBox'
import FlowModalUI from '../../shared/FlowModalUI'
import { SigningTypes } from '../../../../../util/paymentModel'
import {
  Features,
  isDisabled,
  isEnabledForStoreOrRepresentativeId,
  isEnabledForValue,
} from '../../../../../models/features'
import { SignDigitalAgreement } from './SignDigitalAgreement'
import { getEIDProviderName } from '../../../../../models/countries'
import SigningModalCancel from './SigningModalCancel'

export const SigningTabs = ({
  paymentResult,
  onNext,
  authorities,
  onUpdateApplication,
  isLoading,
  onPageLoading,
  payApplyData,
  onCompleteSigning,
  onPageHalt,
  cancelSigning,
  isAutomaticDebitEnabled,
  delayedPaymentEnabled,
}) => {
  const [isModalOpen, setModalOpen] = useState(false)
  const onNextRef = useRef(onNext)

  useEffect(() => {
    onNextRef.current = onNext
  }, [onNext])

  const getRepresentativeIdFromPaymentResult = () =>
    payApplyData?.payment?.paymentResult?.store?.representativeId

  const getStoreIdFromPaymentResult = () =>
    payApplyData?.payment?.paymentResult?.store?.storeId

  const validateAmountIfDebit = () => {
    if (!paymentResult.application || !paymentResult.payment) {
      return ''
    }
    const { selectedAmount: debitAmount, selectedPaymentMethod } =
      payApplyData.current

    const minLimit = selectedPaymentMethod
      ? Math.max(1, selectedPaymentMethod.minPurchaseLimit)
      : 1
    const maxLimit = paymentResult.application.approvedAmount

    if (debitAmount < minLimit) {
      return `${getText('payApply.paymentMethod.creditToSmall')} ${getText(
        'default.currency',
        minLimit,
      )}`
    }
    if (debitAmount > maxLimit) {
      return `${getText(
        'payApply.paymentMethod.newCreditNeededText',
      )} ${getText('default.currency', maxLimit)}`
    }
    return ''
  }

  const [debitAmountError, setDebitAmountError] = useState('')

  useEffect(
    () => setDebitAmountError(validateAmountIfDebit()),
    [payApplyData.current, paymentResult],
  )

  const applicationSummary = (
    <ApplicationSummaryBox
      result={paymentResult}
      debitReference={payApplyData.current.debitReference}
      onPageLoading={onPageLoading}
      updateApplication={onUpdateApplication}
      errorMessage={debitAmountError}
    />
  )

  const isPayment = !!payApplyData?.payment?.paymentResult

  const bankIdTab = {
    type: SigningTypes.ELECTRONIC,
    value: getEIDProviderName(paymentResult.store.country),
    content: (
      <>
        {applicationSummary}
        <SignDigitalPageUI
          debitAmountError={!!debitAmountError}
          isLoading={isLoading}
          onNext={onNext}
          setModalOpen={setModalOpen}
          customer={paymentResult.customer}
          storeName={paymentResult.store.storeName}
          paymentId={paymentResult.id}
          isDelayedPaymentActive={
            isPayment &&
            !isAutomaticDebitEnabled &&
            (delayedPaymentEnabled ||
              isEnabledForValue(
                Features.ACTIVATED_COUNTRIES_FOR_DELAYED_PAYMENT,
                paymentResult?.store?.country,
              ) ||
              isEnabledForStoreOrRepresentativeId(
                Features.ACTIVATED_REPRESENTATIVES_FOR_DELAYED_PAYMENT,
                getStoreIdFromPaymentResult(),
                getRepresentativeIdFromPaymentResult(),
              ) ||
              isEnabledForStoreOrRepresentativeId(
                Features.ACTIVATED_STORES_FOR_DELAYED_PAYMENT,
                getStoreIdFromPaymentResult(),
                getRepresentativeIdFromPaymentResult(),
              ))
          }
        />
      </>
    ),
  }
  const manualSignTab = {
    type: SigningTypes.PHYSICAL,
    value: getText('payApply.sign.paper'),
    content: (
      <>
        {applicationSummary}
        <DisabledOverlay disable={!!debitAmountError}>
          <ApplicationSignPageAnalogUI
            disabled={!!debitAmountError}
            result={paymentResult}
            data={payApplyData}
            authorities={authorities}
            onNext={() => {
              onNextRef.current({
                selectedSigningMethod: SigningTypes.PHYSICAL,
              })
            }}
          />
        </DisabledOverlay>
      </>
    ),
  }
  const digitalAgreementTab = {
    type: SigningTypes.DIGITAL_AGREEMENT,
    feature: Features.DIGITAL_AGREEMENT,
    value: getText('payApply.sign.digitalAgreement.tabTitle'),
    content: (
      <>
        {applicationSummary}
        <DisabledOverlay disable={!!debitAmountError}>
          <SignDigitalAgreement
            disabled={!!debitAmountError}
            onNext={data => {
              setModalOpen(true)
              onNextRef.current({
                selectedSigningMethod: SigningTypes.DIGITAL_AGREEMENT,
                ...data,
              })
            }}
            isNewPageLoading={isLoading}
            customer={paymentResult.customer}
            coCustomer={paymentResult.coCustomer}
          />
        </DisabledOverlay>
      </>
    ),
  }

  const [tabs, setTabs] = useState([])

  useEffect(() => {
    const availableSigningTypes = paymentResult.signing.availableSigningTypes
    const newTabs =
      !isDisabled(Features.DIGITAL_AGREEMENT) &&
      availableSigningTypes.includes(SigningTypes.DIGITAL_AGREEMENT)
        ? [digitalAgreementTab]
        : [bankIdTab, manualSignTab].filter(tab =>
            availableSigningTypes.includes(tab.type),
          )
    setTabs(newTabs)
  }, [
    paymentResult.signing.availableSigningTypes,
    payApplyData.wizard.newPageLoadedAt,
    payApplyData.wizard.onPageLoading,
  ])

  const isDigitalAgreement =
    paymentResult.signing.availableSigningTypes.includes(
      SigningTypes.DIGITAL_AGREEMENT,
    )

  return (
    <>
      <Conditional when={!!isModalOpen}>
        <FlowModalUI
          show
          data={payApplyData}
          onComplete={onCompleteSigning}
          onClose={() => {
            setModalOpen(false)
            onPageHalt()
            if (isDigitalAgreement) {
              cancelSigning()
            }
          }}
          customCancelComponent={isDigitalAgreement ? SigningModalCancel : ''}
          digitalAgreementOnNext={data => {
            setModalOpen(true)
            return onNext({
              selectedSigningMethod: SigningTypes.DIGITAL_AGREEMENT,
              ...data,
            })
          }}
          customer={paymentResult.customer}
          coCustomer={paymentResult.coCustomer}
        />
      </Conditional>
      <Tabs fullWidth values={tabs} />
    </>
  )
}
